// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module FadeIn
 * @package @geenius/analytics/solidjs
 * @description Provides a local SolidJS animation wrapper so the analytics
 * variant can keep lightweight entrance motion without depending on an
 * out-of-matrix runtime package.
 */

import type { Component, JSX } from "solid-js";
import { splitProps } from "solid-js";
import { cn } from "./cn";

export interface FadeInProps extends JSX.HTMLAttributes<HTMLDivElement> {
  delay?: number;
}

function createDelayStyle(
  style: JSX.CSSProperties | string | undefined,
  delay: number | undefined,
): JSX.CSSProperties | string | undefined {
  if (!delay) {
    return style;
  }

  if (typeof style === "string") {
    return `${style}; animation-delay: ${delay}ms`;
  }

  return {
    ...style,
    "animation-delay": `${delay}ms`,
  };
}

export const FadeIn: Component<FadeInProps> = (props) => {
  const [local, rest] = splitProps(props, [
    "children",
    "class",
    "delay",
    "style",
  ]);

  return (
    <div
      {...rest}
      class={cn(
        "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-300",
        local.class,
      )}
      style={createDelayStyle(local.style, local.delay)}
    >
      {local.children}
    </div>
  );
};
